import { container, device, events } from '@game-studio/arcade-base';
import { Container, Rectangle, Text } from 'pixi.js-legacy';
import { GameApplication } from './application';
import { BackgroundView } from './background-view';
import { InstantPrizeIndicatorView } from './instant-prize-indicator-view';
import { GameBoardView } from './game-board-view';
import { gameModel } from '../../models/game-model';
import { IDONDStrings, ISkinConfigEx } from '@/src/types';
import { CollectorView, OrientationType } from './collector/collector-view';
import { soundService } from '../../services/sound-service';

const LANDSCAPE_GAME_WIDTH: number = 1136;
const LANDSCAPE_GAME_HEIGHT: number = 640;

const PORTRAIT_GAME_WIDTH: number = 412;
const PORTRAIT_GAME_HEIGHT: number = 915;

export class GameView extends Container {
    public static GAME_BOARD_BOUND: Rectangle = new Rectangle();

    private _scaleMin: number = 0;
    private _scaleMax: number = 0;

    private _scaleMinPortraitOffset: number = 1.27;

    private _backgroundView: BackgroundView;
    private _gameBoardView: GameBoardView;
    private _collectorLandscapeView: CollectorView;
    private _collectorPortraitView: CollectorView;
    private _instantPrizeIndicatorView: InstantPrizeIndicatorView;
    private _disclaimerText: Text;

    constructor() {
        super();

        this._backgroundView = new BackgroundView();
        this.addChild(this._backgroundView);

        this._collectorLandscapeView = new CollectorView(OrientationType.LANDSCAPE);
        this.addChild(this._collectorLandscapeView);

        this._collectorPortraitView = new CollectorView(OrientationType.PORTRAIT);
        this.addChild(this._collectorPortraitView);

        this._gameBoardView = new GameBoardView();
        this.addChild(this._gameBoardView);

        this._instantPrizeIndicatorView = new InstantPrizeIndicatorView();
        this.addChild(this._instantPrizeIndicatorView);

        this._disclaimerText = new Text((container.strings as IDONDStrings).disclaimer);
        this._disclaimerText.style.fill = 0xffffff;
        this._disclaimerText.style.fontFamily = 'Wallop-Bold';
        this._disclaimerText.style.fontSize = 12.2;
        this.addChild(this._disclaimerText);

        this.addListeners();
    }

    public start(): void {
        // Start Music and Intro.
        soundService.beginMusic();
        this._backgroundView.start();
    }

    public draw(): void {
        this.calculateScales();

        if (device.orientation.isLandscape || GameApplication.portraitMode) {
            this.drawLandscape();
        } else {
            this.drawPortrait();
        }

        this.scale.set(1 / GameApplication.pixiApp.renderer.resolution);
    }

    private drawLandscape(): void {
        this._backgroundView.scale.set(this._scaleMax);
        this._backgroundView.position.set(GameApplication.pixiApp.renderer.width * 0.5, GameApplication.pixiApp.renderer.height * 0.5);

        this._gameBoardView.preDraw();
        this._gameBoardView.scale.set(this._scaleMin);
        const gameBoardX: number = GameApplication.pixiApp.renderer.width * 0.5 - this._gameBoardView.width * 0.5;
        const gameBoardY: number = GameApplication.pixiApp.renderer.height * 0.13;
        this._gameBoardView.position.set(gameBoardX, gameBoardY);
        this.updateGameBoardBounds(gameBoardX, gameBoardY, this._gameBoardView.width, this._gameBoardView.height);
        this._gameBoardView.postDraw();

        this._collectorPortraitView.alpha = 0;
        this._collectorLandscapeView.alpha = 1;
        this._collectorLandscapeView.scale.set(this._scaleMin);
        this._collectorLandscapeView.draw();

        const instantPrizeX: number = GameView.GAME_BOARD_BOUND.x + GameView.GAME_BOARD_BOUND.width * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].x;
        const instantPrizeY: number = GameView.GAME_BOARD_BOUND.y + GameView.GAME_BOARD_BOUND.height * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].y;
        this._instantPrizeIndicatorView.scale.set(this._scaleMin * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].scaleMultiplier);
        this._instantPrizeIndicatorView.position.set(instantPrizeX, instantPrizeY);

        this._disclaimerText.visible = true;
        this._disclaimerText.scale.set(this._scaleMin);
        const disclaimerX: number = GameApplication.pixiApp.renderer.width * 0.5 - this._disclaimerText.width * 0.5;
        const disclaimerY: number = GameApplication.pixiApp.renderer.height - this._disclaimerText.height * 1.25;
        this._disclaimerText.position.set(disclaimerX, disclaimerY);
    }

    private drawPortrait(): void {
        const portraitScaleMinOffset = this._scaleMin * this._scaleMinPortraitOffset;

        this._backgroundView.scale.set(this._scaleMax / 2);
        this._backgroundView.position.set(GameApplication.pixiApp.renderer.width * 0.5, GameApplication.pixiApp.renderer.height * 0.5);

        this._gameBoardView.preDraw();
        this._gameBoardView.scale.set(portraitScaleMinOffset * 1.05);
        const gameBoardX: number = GameApplication.pixiApp.renderer.width * 0.5 - this._gameBoardView.width * 0.5;
        const gameBoardY: number = GameApplication.pixiApp.renderer.height * 0.13;
        this._gameBoardView.position.set(gameBoardX, gameBoardY);
        this.updateGameBoardBounds(gameBoardX, gameBoardY, this._gameBoardView.width, this._gameBoardView.height);
        this._gameBoardView.postDraw();

        this._collectorLandscapeView.alpha = 0;
        this._collectorPortraitView.alpha = 1;
        this._collectorPortraitView.scale.set(portraitScaleMinOffset);
        this._collectorPortraitView.draw();

        const instantPrizeX: number = GameView.GAME_BOARD_BOUND.x + GameView.GAME_BOARD_BOUND.width * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].x;
        const instantPrizeY: number = GameView.GAME_BOARD_BOUND.y + GameView.GAME_BOARD_BOUND.height * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].y;
        this._instantPrizeIndicatorView.scale.set(portraitScaleMinOffset * (container.skin.config as ISkinConfigEx).instantPrize[device.orientation.value].scaleMultiplier);
        this._instantPrizeIndicatorView.position.set(instantPrizeX, instantPrizeY);

        this._disclaimerText.visible = false;
    }

    private updateGameBoardBounds(x: number, y: number, width: number, height: number): void {
        GameView.GAME_BOARD_BOUND.x = x;
        GameView.GAME_BOARD_BOUND.y = y;
        GameView.GAME_BOARD_BOUND.width = width;
        GameView.GAME_BOARD_BOUND.height = height;
    }

    private calculateScales(): void {
        const GAME_WIDTH = device.orientation.isLandscape || GameApplication.portraitMode ? LANDSCAPE_GAME_WIDTH : PORTRAIT_GAME_WIDTH;
        const GAME_HEIGHT = device.orientation.isLandscape || GameApplication.portraitMode ? LANDSCAPE_GAME_HEIGHT : PORTRAIT_GAME_HEIGHT;
        this._scaleMin = Math.min(GameApplication.pixiApp.renderer.width / GAME_WIDTH, GameApplication.pixiApp.renderer.height / GAME_HEIGHT);
        this._scaleMax = Math.max(GameApplication.pixiApp.renderer.width / GAME_WIDTH, GameApplication.pixiApp.renderer.height / GAME_HEIGHT);
    }

    private addListeners() {
        events.CABINET.START_REVEAL_ALL.addListener(() => {
            gameModel.autoPlay = true;
        }, this);
        events.CABINET.STOP_REVEAL_ALL.addListener(() => {
            gameModel.autoPlay = false;
        }, this);
        events.GAME.COMPLETE.addListener(() => {
            gameModel.autoPlay = false;
        }, this);
    }
}
